import { useCallback } from 'react'

const BOOL_FILTERS = [
  'returnable',
  'has_media',
  'memo',
  'is_sustainable',
  'include_unavailable',
  'is_manual'
]
const ARRAY_FILTERS = [
  'shape',
  'cut_code_shape',
  'color',
  'clarity',
  'fluorescence',
  'symmetry_grade',
  'polish_grade',
  'type',
  'cut_code',
  'lab',
  'eye_clean',
  'cut_grade',
  'tone',
  'luster',
  'treatments'
]

enum GemstoneStoneType {
  Sapphire = 'sapphire',
  Emerald = 'emerald',
  Ruby = 'ruby'
}
const GEMSTONE_STONE_TYPE_VALUES = Object.values(GemstoneStoneType)

type SearchParams = Record<string, undefined | string | (string | null)[] | number | boolean>

export const useSearchParamsProps = ({
  stoneTypeRedirect
}: {
  stoneTypeRedirect?: boolean
} = {}): {
  getDefaultParams: (search: string) => SearchParams | undefined
  onSelectedDiamondChange: (filters: { sku: any }) => void
} => {
  const getDefaultParams = (search: string): SearchParams | undefined => {
    const pathname = window.location.pathname;
    const urlSearchParams = new URLSearchParams(search)

    const params: SearchParams = {
      page: 1,
      limit: 25
    }

    if (stoneTypeRedirect) {
      const stoneType = pathname.slice(pathname.lastIndexOf('/') + 1) as GemstoneStoneType
      if (GEMSTONE_STONE_TYPE_VALUES.includes(stoneType)) {
        params.stone_type = stoneType
      }
    }

    const queryString = urlSearchParams.get('filters')
    const searchParams = queryString !== null && queryString !== '' ? JSON.parse(atob(queryString)) : []

    for (const key in searchParams) {
      if (typeof searchParams[key] === 'function') continue
      const value = searchParams[key].toString()
      if (value === '') continue
      if (key === 'page' || key === 'limit') {
        params[key] = +value
      } else if (params[key]) {
        const previous = Array.isArray(params[key]) ? params[key] : [params[key] as string]
        const next = value.includes(',') ? value.split(',') : [value]
        params[key] = [...previous, ...next]
      } else {
        params[key] = value.includes(',') ? value.split(',') : value
      }
    }

    BOOL_FILTERS.forEach(key => {
      if (params[key]) params[key] = params[key] === 'true'
    })

    ARRAY_FILTERS.forEach(key => {
      if (params[key]) {
        params[key] = Array.isArray(params[key]) ? params[key] : [params[key] as string]
      }
    })

    if (Array.isArray(params.eye_clean) && params.eye_clean.includes('')) {
      params.eye_clean = params.eye_clean.map(value => (value === '' ? null : value))
    }
    if (Array.isArray(params.luster) && params.luster.includes('')) {
      params.luster = params.luster.map(value => (value === '' ? null : value))
    }
    if (Array.isArray(params.clarity) && params.clarity.includes('')) {
      params.clarity = params.clarity.map(value => (value === '' ? null : value))
    }
    if (Array.isArray(params.treatments) && params.treatments.includes('')) {
      params.treatments = params.treatments.map(value => (value === '' ? null : value))
    }
    if (Array.isArray(params.culet_size) && params.culet_size.includes('')) {
      params.culet_size = params.culet_size.map(value => (value === '' ? null : value))
    }
    if (Array.isArray(params.girdle) && params.girdle.includes('')) {
      params.girdle = params.girdle.map(value => (value === '' ? null : value))
    }
    if (Array.isArray(params.shade) && params.shade.includes('')) {
      params.shade = params.shade.map(value => (value === '' ? null : value))
    }
    return params
  }

  const onSelectedDiamondChange = useCallback((filters: { sku: any }): void => {
    const params = {
      sku: filters.sku
    }
    const searchParams = new URLSearchParams(window.location.search)
    const queryString = btoa(JSON.stringify(params))
    searchParams.set('filters', queryString)
    const newUrl = `${window.location.pathname}?${searchParams.toString()}`
    window.history.replaceState(null, '', newUrl)
  }, [])

  return { getDefaultParams, onSelectedDiamondChange }
}
